import { Link } from '@tanstack/react-router';
import {
  docVersionIndexRoute,
  LATEST_DEVELOPER_VERSION,
  type DocGroupKey,
} from './docGroupRoutes';

interface DocsVersionNoticeProps {
  group: DocGroupKey;
  /** The version of the page being read; undefined for unversioned pages. */
  version?: string;
}

/**
 * Banner shown on documentation for a superseded API version. Every v1 page
 * still reads as current on its own — nothing in the prose says "this is the
 * old one" — so the reader needs a standing marker and a way across, rather
 * than a note hand-written into each page.
 */
export function DocsVersionNotice({ group, version }: DocsVersionNoticeProps) {
  if (group !== 'developers' || !version || version === LATEST_DEVELOPER_VERSION) {
    return null;
  }

  return (
    <div className="docs-callout docs-callout-info" role="note">
      <p className="docs-callout-title">
        <span>API {version} is in long-term support</span>
      </p>
      <div className="docs-callout-body">
        <p>
          This page documents API {version}. It keeps working, and existing integrations
          don't need to move, but new development belongs on{' '}
          <Link to={docVersionIndexRoute(group, LATEST_DEVELOPER_VERSION)}>
            API {LATEST_DEVELOPER_VERSION}
          </Link>
          . Authentication via xAuth is deprecated and will be removed on September 30,
          2027.
        </p>
      </div>
    </div>
  );
}

export default DocsVersionNotice;
